import { useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

import { HistoriaNaMesa } from '@/historias/mesa';
import { useAbaixoDe } from '@/lib/largura';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { AlteracoesNoPainel, SELETOR_DE_CONTROLE, valorDoControle } from './alteracoes-no-painel';
import { Botao, BotaoFechar } from './Botao';
import { fileteDasAbas } from './filete';
import { ContextoDoPainel } from './contextoDoPainel';
import { armazemDoNavegador, chaveDaForma, gravarForma, lerForma, type FormaDoPainel } from './forma-do-painel';
import { Janela } from './JanelaDeAcao';

/**
 * Painel de detalhe (v2.1, p.28). Abre ao clicar numa linha, sem sair da lista
 * e sem recarregar. FLUTUA: raio 20, elevacao 3, a 12 px da borda. E a ORDEM E
 * FIXA: identidade, dados-chave, acoes, abas.
 *
 * A ordem nao e diagramacao, e memoria muscular: quem abre trinta paineis por
 * dia para de ler e passa a olhar no lugar onde a informacao sempre esteve. Um
 * painel que muda de ordem por modulo devolve todo mundo a leitura.
 *
 * No maximo QUATRO ABAS e DUAS ACOES em destaque. O painel e a segunda tela,
 * nao um formulario inteiro espremido em 400 px.
 *
 * Fecha de tres jeitos, e os tres sao obrigatorios: "✕", `Esc` e clique fora.
 * O "✕" e o caractere — o vocabulario de icones do manual nao tem entrada para
 * "fechar", e inventar uma aqui seria escolher icone de lado numa tela.
 *
 * A LISTA DE ORIGEM NAO CONTA COMO "FORA"
 * Clicar em outra linha da lista troca o conteudo do painel; nao o fecha. Isso
 * e o que o manual quer quando diz que o painel "abre sem recarregar a lista, e
 * a linha de origem fica marcada". Quem marca a lista como origem e a propria
 * pagina, com `data-painel-origem`. Isso vale com o painel FIXO AO LADO, de
 * 1280 px para cima.
 *
 * R01 (p.28): fixo ao lado da tabela so a partir de 1280 px — 400 px em XL
 * (1600+), 360 px em L. Abaixo de 1280 vira GAVETA sobre a tabela, com o fundo
 * escurecido (preto obra a 40%) e ALTURA TOTAL, por cima da barra superior
 * (p.29): 400 px em M, 100% menos 64 px em S (768-1023) e tela cheia em XS.
 * Na gaveta o veu cobre a lista: o clique nele FECHA o painel, como em toda
 * sobreposicao (R01, p.29), e trocar de linha pede fechar antes.
 *
 * GAVETA E EXPANDIDO BLOQUEIAM A PAGINA (p.29): `aria-modal` e o Tab gira
 * dentro do painel, como na gaveta do menu. Fixo ao lado, de 1280 px para
 * cima, ele e parte da pagina e o Tab passa normalmente.
 *
 * O FOCO VAI E VOLTA
 * Abrir pelo teclado (Enter na celula de identidade) e chegar ao painel eram
 * duas coisas diferentes: o `<aside>` e irmao POSTERIOR do conteudo, entao
 * alcanca-lo exigia tabular por todas as linhas restantes da tabela, pela
 * paginacao e pelo seletor de itens por pagina. E fechar com `Esc` largava o
 * foco no `<body>`, de onde se recomeca do topo da pagina. Agora o painel puxa
 * o foco ao abrir e o devolve ao elemento de origem ao fechar — sem valor novo
 * e sem token.
 *
 * EXPANDIR (melhoria pedida pelo dono em 24/09)
 * Preencher formulario em 400 px aperta. "Expandir" leva o MESMO painel para
 * uma janela centralizada e larga, com um veu atras. O botao e texto, e nao
 * icone, pelo mesmo motivo do "✕": o vocabulario do manual nao tem entrada
 * para "expandir".
 *
 * ABRE EXPANDIDO (pedido do dono, 31/10/2026)
 * "Quando eu clico em um equipamento faz sentido que o painel dele ja venha
 * expandido, pois os mesmos dados que eu tenho na linha do equipamento eu
 * tenho na visao lateral." A coluna de 360-400 px repetia a linha e apertava
 * a edicao; agora o painel abre na janela larga, e o conteudo usa a largura
 * (grade de ate tres colunas, `grade-do-painel`, medida pelo proprio painel).
 *
 *  - QUEM REDUZ TEM A ESCOLHA LEMBRADA, por pessoa e por tela, neste navegador
 *    (`forma-do-painel.ts`). So o botao "Reduzir painel"/"Expandir painel"
 *    grava; o recuo que uma janela aberta de dentro do painel pede (nunca duas
 *    modais empilhadas, p.29) e emprestado e volta ao fechar a janela, e o
 *    "Editar ficha" que expande para caber o formulario tambem nao grava.
 *    A tela que precisar do painel estreito por padrao passa `formaInicial`.
 *  - `Esc` E CLIQUE FORA FECHAM, nos dois modos. Antes, expandido, eles so
 *    reduziam, para ninguem perder o que digitou por um clique no veu; com a
 *    janela sendo o padrao, reduzir virou surpresa. O que protege a digitacao
 *    agora e a PERGUNTA: com algum campo alterado e nao gravado
 *    (`alteracoes-no-painel.ts`), `Esc`, clique fora e "✕" perguntam
 *    "Descartar o que foi digitado?" antes de fechar.
 *  - A PERGUNTA NAO RECUA O PAINEL: e sobre fechar este painel, e a pessoa
 *    precisa ver o que perderia. Com ela (ou qualquer janela) aberta, o veu
 *    do painel sai e o fundo escurecido e o da janela: um veu so.
 *  - Abaixo de 1280 px, reduzido continua sendo gaveta, e abaixo de 768 px os
 *    dois modos sao tela cheia (o botao de expandir nem aparece).
 */

export interface AbaPainel {
  readonly id: string;
  readonly rotulo: string;
  readonly conteudo: ReactNode;
  /** O código da história da aba (modo histórias, p.47): o marcador fica colado a ela. */
  readonly historia?: string;
}

const MAXIMO_DE_ABAS = 4;
const MAXIMO_DE_ACOES = 2;

export function PainelDetalhe({
  codigo,
  titulo,
  subtitulo,
  marcadores,
  dadosChave,
  abas,
  acoes = [],
  notaDasAcoes,
  formaInicial = 'expandido',
  memoria,
  aoFechar,
}: {
  /**
   * O codigo da identidade, quando ele e SEPARADO do nome: vai pequeno, em
   * monoespacada, acima do titulo — como no exemplo da p.19 ("ESC-001" sobre
   * "Escavadeira CAT 320"). Com ele, `titulo` e o nome. Sem ele, `titulo`
   * continua sendo a identidade inteira.
   */
  readonly codigo?: string;
  /** A identidade: o codigo do equipamento, a O.S., o numero da nota. */
  readonly titulo: string;
  readonly subtitulo: string;
  /** Badges de status e prioridade. Ficam na identidade, nao nos dados-chave. */
  readonly marcadores?: ReactNode;
  readonly dadosChave: ReactNode;
  readonly abas: readonly AbaPainel[];
  /** No maximo duas. Array, e nao `children`, justamente para poder contar. */
  readonly acoes?: readonly ReactNode[];
  /**
   * Por que as acoes estao como estao, em texto VISIVEL.
   *
   * Botao desligado com o motivo so no `title` nao avisa ninguem: elemento
   * `disabled` nao recebe foco, nao dispara dica por teclado e e pulado pelo
   * leitor de tela. O resultado e o oposto do pretendido — botao cinza que
   * parece defeito de permissao.
   */
  readonly notaDasAcoes?: string;
  /**
   * Como o painel abre quando a pessoa ainda nao escolheu nesta tela. O padrao
   * e a janela larga (pedido do dono, 31/10/2026); a tela de conferencia linha
   * a linha, que precisa da lista a vista, pode pedir `'reduzido'`.
   */
  readonly formaInicial?: FormaDoPainel;
  /** A chave da escolha lembrada. O padrao e o endereco da tela. */
  readonly memoria?: string;
  readonly aoFechar: () => void;
}) {
  const painel = useRef<HTMLElement>(null);
  const abas_ = useRef<(HTMLButtonElement | null)[]>([]);
  /* Quem tinha o foco quando o painel abriu. E para ca que ele volta. */
  const origem = useRef<Element | null>(null);
  const base = useId();
  const [abaAtiva, setAbaAtiva] = useState<string>(() => abas[0]?.id ?? '');
  /* A escolha lembrada de quem esta logado, nesta tela (ver o comentario do
     componente). A chave e lida UMA vez, ao abrir: trocar de pessoa ou de
     tela e abrir outro painel. */
  const pessoa = useSessaoEmMemoria()?.usuarioId ?? null;
  const [chave] = useState(() => chaveDaForma(pessoa, memoria ?? (typeof window === 'undefined' ? '/' : window.location.pathname)));
  const [expandido, setExpandido] = useState(() => lerForma(armazemDoNavegador(), chave, formaInicial) === 'expandido');
  /* O espelho de `expandido` para o contexto, que e memorizado uma vez so. */
  const expandidoAgora = useRef(expandido);
  expandidoAgora.current = expandido;
  /* O recuo emprestado a uma janela: quando ela fecha, o painel volta. */
  const emprestado = useRef(false);
  /* Abaixo de 1280 px o painel e gaveta (R01, p.28); expandido, janela. Nos
     dois casos ele bloqueia a pagina atras (p.29). */
  const gaveta = useAbaixoDe(1280);
  const bloqueia = expandido || gaveta;
  const contexto = useMemo(
    () => ({
      reduzir: () => {
        if (!expandidoAgora.current) return () => undefined;
        emprestado.current = true;
        setExpandido(false);
        return () => {
          if (!emprestado.current) return;
          emprestado.current = false;
          setExpandido(true);
        };
      },
      expandir: () => {
        emprestado.current = false;
        setExpandido(true);
      },
    }),
    [],
  );

  /** O botao de quem escolhe: troca a forma e a guarda para a proxima vez. */
  function escolherForma(forma: FormaDoPainel) {
    emprestado.current = false;
    setExpandido(forma === 'expandido');
    gravarForma(armazemDoNavegador(), chave, forma);
  }

  /* O QUE FOI DIGITADO E NAO GRAVADO (ver `alteracoes-no-painel.ts`). O valor
     de cada campo e guardado quando a pessoa chega nele; gravacao aceita pelo
     servidor esquece tudo. */
  const [alteracoes] = useState(
    () =>
      new AlteracoesNoPainel<Element>(valorDoControle, (controle) => controle.isConnected && (painel.current?.contains(controle) ?? false)),
  );
  const [confirmando, setConfirmando] = useState(false);
  const clienteDasConsultas = useQueryClient();
  useEffect(
    () =>
      clienteDasConsultas.getMutationCache().subscribe((evento) => {
        if (evento.type === 'updated' && evento.action.type === 'success') alteracoes.esquecer();
      }),
    [clienteDasConsultas, alteracoes],
  );
  function lembrarDoControle(alvo: EventTarget) {
    if (!(alvo instanceof Element)) return;
    const controle = alvo.closest(SELETOR_DE_CONTROLE);
    if (controle) alteracoes.lembrar(controle);
  }

  /* Os tres jeitos de fechar passam por aqui. A referencia muda a cada render
     (le `aoFechar`), e os ouvintes abaixo a pegam pela ref, para nao serem
     refeitos a cada tecla. */
  function pedirParaFechar() {
    if (alteracoes.temAlteracao()) setConfirmando(true);
    else aoFechar();
  }
  const pedirParaFecharAgora = useRef(pedirParaFechar);
  pedirParaFecharAgora.current = pedirParaFechar;

  if (import.meta.env.DEV && abas.length > MAXIMO_DE_ABAS) {
    console.error(
      `PainelDetalhe "${titulo}": ${abas.length} abas. O manual (p.20) permite no máximo ${MAXIMO_DE_ABAS}.`,
    );
  }
  if (import.meta.env.DEV && acoes.length > MAXIMO_DE_ACOES) {
    console.error(
      `PainelDetalhe "${titulo}": ${acoes.length} ações. O manual (p.20) permite no máximo ${MAXIMO_DE_ACOES} em destaque.`,
    );
  }

  /* Verificador da hierarquia da p.19, em desenvolvimento: codigo pequeno em
     monoespacada acima, NOME como titulo. O componente aceita as duas coisas
     separadas (`codigo` + `titulo`); passar o codigo como titulo devolve a
     hierarquia invertida ("CAM-001" em 21 px e o nome em 13 px de apoio). */
  useEffect(() => {
    if (import.meta.env.DEV && codigo === undefined && /^[A-Z]{2,4}-\d+/.test(titulo)) {
      console.warn(
        `PainelDetalhe "${titulo}": o título tem cara de código. A p.19 põe o código pequeno acima (prop \`codigo\`) e o nome como título.`,
      );
    }
  }, [codigo, titulo]);

  /* Efeito proprio, com lista de dependencias VAZIA: ele roda uma vez por
     painel aberto. Juntar isto ao efeito do `Esc` (que depende de `aoFechar`)
     faria o foco pular de volta para a lista a cada re-renderizacao. */
  useEffect(() => {
    const no = painel.current;
    origem.current = document.activeElement;
    no?.focus();

    return () => {
      /* SO devolve o foco se ele ainda estiver aqui dentro (ou no `body`, que e
         onde ele cai quando o painel fecha com `Esc`). Sem esta guarda, fechar
         o painel clicando no campo de busca roubava de volta o foco que a
         pessoa acabara de dar ao campo. */
      const foco = document.activeElement;
      const aindaAqui = foco === null || foco === document.body || (no?.contains(foco) ?? false);
      if (!aindaAqui) return;

      const alvo = origem.current;
      if (alvo instanceof HTMLElement && document.contains(alvo)) alvo.focus();
    };
  }, []);

  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return;
      /* O `Esc` que fecha o dialogo de confirmacao de dentro do painel nao
         pode tambem fechar o painel. */
      if (evento.defaultPrevented) return;
      /* O `<dialog>` nativo fecha no `Esc` como acao padrao, DEPOIS do
         `keydown` — que chega aqui sem `defaultPrevented`. Com o dialogo
         aberto, o foco esta preso dentro dele: o alvo diz de onde veio. */
      if (evento.target instanceof Element && evento.target.closest('dialog[open]')) return;
      /* O `Esc` que fecha um menu aberto (Mais acoes, lista de escolha) fecha
         o menu, e nao o painel inteiro. */
      if (evento.target instanceof Element && evento.target.closest('[aria-haspopup][aria-expanded="true"]')) return;
      /* O `Esc` e deste painel. Sem o `preventDefault`, a pergunta de
         descartar abria e a acao padrao do MESMO `Esc` (o pedido de fechar do
         `<dialog>`) a fechava no mesmo instante. */
      evento.preventDefault();
      pedirParaFecharAgora.current();
    }

    function aoApontar(evento: PointerEvent) {
      const alvo = evento.target;
      if (!(alvo instanceof Element)) return;
      if (painel.current?.contains(alvo)) return;
      /* O clique DENTRO de uma janela aberta (a confirmacao que nasce do
         painel, ou qualquer outra) nao e clique fora do painel — como o `Esc`
         acima. Sem isto, a janela que vive fora do DOM do painel o fechava.
         O toast (fechar, "Ver") e a lista que flutua tambem nao sao fora. */
      if (alvo.closest('dialog[open]')) return;
      if (alvo.closest('.pilha-de-toasts, [data-camada-de-cima]')) return;
      /* Trocar de linha na lista de origem troca o painel; nao o fecha. Com o
         painel expandido o veu cobre a lista, e o clique cai nele. */
      if (!expandidoAgora.current && alvo.closest('[data-painel-origem]')) return;
      pedirParaFecharAgora.current();
    }

    document.addEventListener('keydown', aoTeclar);
    document.addEventListener('pointerdown', aoApontar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('pointerdown', aoApontar);
    };
  }, []);

  /* O TAB NAO SAI DA GAVETA NEM DA JANELA (R01, p.29). O veu ja bloqueava o
     clique, mas o teclado passava por baixo dele: do painel para a tabela e
     para a barra superior. A mesma armadilha da gaveta do menu. Dentro de um
     `<dialog>` aberto de dentro do painel, quem prende o foco e o dialogo. */
  useEffect(() => {
    if (!bloqueia) return;
    function aoTabular(evento: KeyboardEvent) {
      const no = painel.current;
      if (evento.key !== 'Tab' || !no) return;
      if (evento.target instanceof Element && evento.target.closest('dialog[open]')) return;
      const lista = [
        ...no.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((elemento) => elemento.getClientRects().length > 0 && !elemento.closest('dialog'));
      const primeiro = lista[0];
      const ultimo = lista[lista.length - 1];
      if (!primeiro || !ultimo) return;
      const atual = document.activeElement;
      if (!no.contains(atual) || atual === no) {
        evento.preventDefault();
        (evento.shiftKey ? ultimo : primeiro).focus();
      } else if (evento.shiftKey && atual === primeiro) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && atual === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      }
    }
    document.addEventListener('keydown', aoTabular);
    return () => {
      document.removeEventListener('keydown', aoTabular);
    };
  }, [bloqueia]);

  /* O TOAST ANDA PARA O LADO DO PAINEL (item 12 dos robos): a regra mora no
     `global.css`, `.pilha-de-toasts`, e le esta marca na raiz. */
  useEffect(() => {
    const raiz = document.documentElement;
    raiz.dataset['painelAberto'] = expandido ? 'janela' : 'lado';
    return () => {
      delete raiz.dataset['painelAberto'];
    };
  }, [expandido]);

  const conteudoDaAba = abas.find((aba) => aba.id === abaAtiva)?.conteudo ?? null;

  return (
    <>
    <ContextoDoPainel.Provider value={contexto}>
      {/* O veu da janela expandida: cobre a lista e diz que o foco esta na
          janela. Ele e so fundo — o clique nele cai no `aoApontar`, que fecha
          (ou pergunta, com digitacao pendente). */}
      {/* `veu-do-painel`: com QUALQUER janela aberta por cima (a pergunta de
          descartar, ou a de editar que a pagina abre, como nos cadastros), o
          fundo escurecido e o dela, e este some (`global.css`). Um veu so. */}
      {expandido ? <div aria-hidden className="veu-do-painel fixed inset-0 z-[var(--z-modal)] bg-sobreposicao" /> : null}
      {/* O veu da gaveta, so abaixo de 1280 px: o clique nele cai no
          `aoApontar` fora de `data-painel-origem`, e fecha. */}
      {!expandido ? <div aria-hidden className="veu-do-painel fixed inset-0 z-[var(--z-gaveta)] hidden bg-sobreposicao max-[1280px]:block" /> : null}
      <aside
        ref={painel}
        /* `-1`: recebe foco por codigo, nao entra na ordem do Tab. */
        tabIndex={-1}
        aria-label={`Detalhe de ${codigo ?? titulo}`}
        {...(bloqueia ? { 'aria-modal': true, role: 'dialog' } : {})}
        /* Onde a pessoa chega a um campo, o valor dele e guardado (ver
           `alteracoes-no-painel.ts`): foco, toque ou tecla, o que vier antes.
           Na fase de captura, antes de o campo mudar. */
        onFocusCapture={(evento) => {
          lembrarDoControle(evento.target);
        }}
        onPointerDownCapture={(evento) => {
          lembrarDoControle(evento.target);
        }}
        onKeyDownCapture={(evento) => {
          lembrarDoControle(evento.target);
        }}
        className={
          expandido
            ? /* Janela (p.29): ao centro, raio 24 e elevacao 4, na ALTURA FIXA de
                 85% da tela, para trocar de aba nao fazer a janela pular de
                 tamanho; so o corpo das abas rola. Larga (duas vezes e meia o
                 painel, 1000 px), para a ficha caber em tres colunas sem
                 rolar tanto. Abaixo de 768 px, tela cheia — la o botao de
                 expandir nem aparece, porque o painel ja ocupa a tela. */
              'anima-modal fixed top-1/2 left-1/2 z-[var(--z-modal)] flex h-[var(--modal-altura-max)] w-[calc(var(--painel-xl)*2.5)] max-w-[calc(100vw-var(--esp-12))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-auto rounded-modal bg-flutuante shadow-sobreposicao max-[768px]:inset-0 max-[768px]:h-auto max-[768px]:w-full max-[768px]:max-w-none max-[768px]:translate-0 max-[768px]:rounded-none'
            : /* Fixo ao lado de 1280 px para cima (400 em XL, 360 em L), a 12 px
                 da borda. Abaixo, gaveta sobre a lista: 400 px em M, 100% - 64
                 px em S e tela cheia em XS (p.18, p.28). */
              'my-3 mr-3 flex w-[var(--painel-xl)] shrink-0 flex-col overflow-auto rounded-painel border border-borda-sutil bg-flutuante shadow-flutuante max-[1600px]:w-[var(--painel-l)] max-[1280px]:fixed max-[1280px]:inset-y-0 max-[1280px]:right-0 max-[1280px]:z-[var(--z-gaveta)] max-[1280px]:m-3 max-[1280px]:w-[var(--painel-xl)] max-[1280px]:anima-gaveta max-[1024px]:w-[calc(100%-var(--spacing)*16)] max-[768px]:m-0 max-[768px]:w-full max-[768px]:rounded-none max-[768px]:border-0'
        }
      >
        {/* 1. Identidade (p.28): o CODIGO em Sora 20 com o selo de status ao
            lado, e Mais acoes e Fechar a direita; abaixo, o nome e a linha de
            apoio. Sem codigo, o titulo e a identidade inteira e vai em Sora.
            O titulo quebra onde for preciso, em ate duas linhas, com o texto
            inteiro no `title`. */}
        <div className="flex flex-col gap-1 p-5 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span
                className="txt-secao line-clamp-2 min-w-0 text-pretty text-tinta [overflow-wrap:anywhere]"
                title={codigo ?? titulo}
              >
                {codigo ?? titulo}
              </span>
              {marcadores ? <span className="flex flex-wrap items-center gap-2">{marcadores}</span> : null}
            </div>
            <div className="-mr-1 flex shrink-0 items-center gap-1">
              {/* Verbo + objeto (p.21). Fantasma pequeno. Abaixo de 768 px o
                  painel ja ocupa a tela: nao ha o que expandir. */}
              <button
                type="button"
                aria-pressed={expandido}
                onClick={() => {
                  escolherForma(expandido ? 'reduzido' : 'expandido');
                }}
                className="botao botao-fantasma botao-p max-[768px]:hidden"
              >
                {expandido ? 'Reduzir painel' : 'Expandir painel'}
              </button>
              <BotaoFechar rotulo="Fechar painel" aoFechar={pedirParaFechar} />
            </div>
          </div>
          {codigo === undefined ? null : (
            <span className="txt-corpo line-clamp-2 text-pretty text-tinta [overflow-wrap:anywhere]" title={titulo}>
              {titulo}
            </span>
          )}
          <span className="txt-apoio text-pretty text-tinta-secundaria [overflow-wrap:anywhere]">{subtitulo}</span>
        </div>

        {/* 2. Dados-chave (p.28): em grade de duas colunas, o rotulo acima e o
            valor em 600. Sem dado-chave, sem a faixa vazia. */}
        {/* Expandido, os dados-chave cabem numa linha so (quatro colunas): o
            corpo das abas fica com a altura que a janela larga ganhou. */}
        {dadosChave ? (
          <div className={`grid gap-x-4 gap-y-3 px-5 pb-4 ${expandido ? 'grid-cols-4 max-[768px]:grid-cols-2' : 'grid-cols-2'}`}>{dadosChave}</div>
        ) : null}

        {/* 3. Acoes (p.28): logo abaixo dos dados — o painel e um contexto
            separado da pagina, e pode ter um primario proprio. */}
        {acoes.length > 0 ? (
          <div className="flex flex-col gap-2 px-5 pb-4">
            {notaDasAcoes ? <p className="txt-apoio text-tinta-secundaria">{notaDasAcoes}</p> : null}
            <div className="flex flex-wrap items-center gap-2">
              {acoes.map((acao, indice) => (
                <span key={indice}>{acao}</span>
              ))}
            </div>
          </div>
        ) : null}

        {/* 3. Abas */}
        <div className="flex min-h-0 flex-auto flex-col">
          {/* ARIA INTEIRA, ou nenhuma. O painel declarava `tablist` e `tab` com
              `aria-selected` e parava ai: sem `tabpanel`, sem `aria-controls` e
              sem navegacao por seta. O leitor de tela anunciava "aba 1 de 3,
              selecionada" e nao sabia dizer a que painel aquilo correspondia —
              ARIA pela metade promete uma semantica que nao se cumpre. */}
          <div
            role="tablist"
            aria-label="Seções do detalhe"
            /* Quatro abas nem sempre cabem em 312 px (o painel de 360 menos o
               recuo), e com o texto em caixa alta a quarta ja nao cabia a
               1440: a barra rolava de lado e cortava "HISTÓRIC" na borda, sem
               aviso. Agora a aba que nao cabe desce para uma segunda linha
               organizada (`barra-de-abas`), e nenhuma palavra e cortada. */
            ref={fileteDasAbas}
            className="barra-de-abas filete-das-abas shrink-0 px-5"
            onKeyDown={(evento) => {
              const passo =
                evento.key === 'ArrowRight' ? 1 : evento.key === 'ArrowLeft' ? -1 : 0;
              if (passo === 0) return;
              evento.preventDefault();
              const atual = abas.findIndex((aba) => aba.id === abaAtiva);
              const proxima = abas[(atual + passo + abas.length) % abas.length];
              if (!proxima) return;
              setAbaAtiva(proxima.id);
              abas_.current[(atual + passo + abas.length) % abas.length]?.focus();
            }}
          >
            {abas.map((aba, indice) => {
              const ativa = aba.id === abaAtiva;
              const botao = (
                <button
                  key={aba.id}
                  ref={(elemento) => {
                    abas_.current[indice] = elemento;
                  }}
                  type="button"
                  role="tab"
                  id={`${base}-aba-${aba.id}`}
                  aria-selected={ativa}
                  aria-controls={`${base}-painel-${aba.id}`}
                  /* Tabulacao rotativa: o Tab entra UMA vez no grupo de abas e as
                     setas andam entre elas. Sem isso, um painel de quatro abas
                     custa quatro Tabs antes do conteudo. */
                  tabIndex={ativa ? 0 : -1}
                  onClick={() => {
                    setAbaAtiva(aba.id);
                  }}
                  className="aba"
                >
                  {aba.rotulo}
                </button>
              );
              return aba.historia ? (
                <HistoriaNaMesa key={aba.id} codigo={aba.historia} className="inline-flex">
                  {botao}
                </HistoriaNaMesa>
              ) : (
                botao
              );
            })}
          </div>
          <div
            role="tabpanel"
            id={`${base}-painel-${abaAtiva}`}
            aria-labelledby={`${base}-aba-${abaAtiva}`}
            /* `@container/painel`: o conteudo das abas mede o PAINEL, e nao a
               janela — a mesma grade e uma coluna no painel estreito e tres
               no expandido (`grade-do-painel`, `global.css`). */
            className="@container/painel min-h-0 flex-auto overflow-auto p-5"
          >
            {conteudoDaAba}
          </div>
        </div>

      </aside>
    </ContextoDoPainel.Provider>
    {/* A PERGUNTA ANTES DE PERDER A DIGITACAO (p.62: "Descartar as alteracoes
        do cadastro?"), na confirmacao do sistema (`Janela`, p.30). Fora do
        contexto do painel de proposito: ela nao o recua (ver o comentario do
        componente). O foco vai para "Continuar editando": o Enter distraido
        nao apaga nada. */}
    <Janela
      titulo="Descartar o que foi digitado?"
      aberta={confirmando}
      aoFechar={() => {
        setConfirmando(false);
      }}
      largura="p"
      acoes={
        <>
          <Botao
            nivel="secundario"
            onClick={() => {
              setConfirmando(false);
            }}
          >
            Continuar editando
          </Botao>
          <button
            type="button"
            className="botao botao-destrutivo-solido"
            onClick={() => {
              alteracoes.esquecer();
              setConfirmando(false);
              aoFechar();
            }}
          >
            Descartar e fechar
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <span className="flex size-12 items-center justify-center rounded-controle bg-atencao-fundo">
          <TriangleAlert aria-hidden="true" className="size-[var(--icone-destaque)] text-atencao" />
        </span>
        <p className="txt-corpo text-pretty text-tinta-secundaria">
          {`Há campos alterados em ${codigo ?? titulo} que ainda não foram gravados. Fechar o painel agora apaga o que você digitou.`}
        </p>
      </div>
    </Janela>
    </>
  );
}

/**
 * Um par rotulo/valor dos dados-chave (p.28): o rotulo pequeno no secundario,
 * acima; o valor em 14/600 embaixo. A grade de duas colunas e do painel.
 */
export function DadoChave({
  rotulo,
  children,
}: {
  readonly rotulo: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="txt-legenda min-w-0 font-normal text-tinta-secundaria [overflow-wrap:anywhere]">{rotulo}</span>
      <span className="txt-corpo min-w-0 font-semibold text-pretty text-tinta [overflow-wrap:anywhere]">{children}</span>
    </div>
  );
}
